import { useCallback, useEffect, useState } from "react";
import { TOPICS, type Topic } from "@/lib/topics";

const STORAGE_KEY = "flaneriet:enabled-topics:v1";

function readStored(): Set<Topic> | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const arr = JSON.parse(raw) as string[];
    const valid = arr.filter((t): t is Topic => (TOPICS as readonly string[]).includes(t));
    return new Set(valid);
  } catch {
    return null;
  }
}

export function useTopicPreferences() {
  // Start altid med alle emner, så SSR og første klient-render er identiske.
  // localStorage læses efter mount (undgår hydration-mismatch).
  const [enabled, setEnabled] = useState<Set<Topic>>(() => new Set(TOPICS));
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const stored = readStored();
    if (stored) setEnabled(stored);
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(enabled)));
    } catch {
      /* noop */
    }
  }, [enabled, loaded]);


  const toggle = useCallback((topic: Topic) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(topic)) next.delete(topic);
      else next.add(topic);
      return next;
    });
  }, []);

  const enableAll = useCallback(() => setEnabled(new Set(TOPICS)), []);
  const disableAll = useCallback(() => setEnabled(new Set()), []);

  const isEnabled = useCallback((topic: Topic) => enabled.has(topic), [enabled]);

  return { enabled, isEnabled, toggle, enableAll, disableAll };
}
